import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import BreadcrumbsJsonLd from '../components/BreadcrumbsJsonLd';
import GlossaryJsonLd from '../components/GlossaryJsonLd';
import OrganizationJsonLd from '../components/OrganizationJsonLd';
import { BookOpen, FileText, BarChart3, Binary, ShieldCheck, Zap, ArrowRight } from 'lucide-react';

const ResearchPaper = ({ title, date, category, excerpt, tags, slug }: { title: string; date: string; category: string; excerpt: string; tags: string[]; slug: string }) => (
  <div className="group p-8 glass rounded-3xl border border-white/5 hover:border-teal-500/30 transition-all duration-500 relative overflow-hidden">
    <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-20 transition-opacity">
      <FileText className="w-24 h-24 text-teal-400" />
    </div>
    <div className="relative z-10 text-left">
      <div className="flex items-center gap-3 mb-6">
        <span className="px-3 py-1 bg-teal-500/10 text-teal-400 text-[10px] font-bold uppercase tracking-wider rounded-lg border border-teal-500/20">
          {category}
        </span>
        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{date}</span>
      </div>
      <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors leading-tight">
        {title}
      </h3>
      <p className="text-slate-400 text-sm leading-relaxed mb-6 line-clamp-3 italic">
        "{excerpt}"
      </p>
      <div className="flex flex-wrap gap-2 mb-8">
        {tags.map((tag, idx) => (
          <span key={idx} className="text-[10px] text-slate-400 border border-white/5 px-2 py-1 rounded-md">
            #{tag}
          </span>
        ))}
      </div>
      <Link to={`/research/${slug}`} className="flex items-center gap-2 text-white font-bold text-xs uppercase tracking-widest group/btn">
        <span>View Methodology</span>
        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform text-teal-500" />
      </Link>
    </div>
  </div>
);

export default function Research() {
  useSEO({
    title: 'Behavioral Research Lab | GreenTick AI Hiring Archive',
    description: 'Explore peer-reviewed papers on AI-driven behavioral capture, N-Step inference, and cognitive psychometrics in talent evaluation.',
    keywords: 'AI research, behavioral science, hiring assessment data, psychometrics, skill assessment, GreenTick Lab',
    canonicalPath: '/research'
  });

  const papers = [
    {
      title: "The Psychometrics of Behavioral Signal Analysis",
      date: "March 2024",
      category: "Psychometrics",
      excerpt: "Moving beyond static answers to measure latent intent. How AI detects verbal stalls and cognitive load markers to differentiate memorized answers from genuine expertise.",
      tags: ["Signal Analysis", "NLP", "Cognitive Load"],
      slug: "psychometrics-ai-interviews"
    },
    {
      title: "Quantifying the 48-Hour Hiring Loop: A Statistical Model",
      date: "February 2024",
      category: "Operations",
      excerpt: "Modeling the throughput efficiency of automated skill verification vs. manual grading. Findings show a 92% reduction in time-to-hire for high-stakes roles.",
      tags: ["Efficiency", "Automation", "Data Science"],
      slug: "48-hour-hiring-loop"
    },
    {
      title: "Mapping Senior-Level Mental Models via N-Step Inference",
      date: "January 2024",
      category: "Structural Reasoning",
      excerpt: "Benchmarking the ability to map a candidate's mental architecture (how they build a system) versus just checking if the code runs.",
      tags: ["Inference", "Skill Mapping", "System Design"],
      slug: "zero-shot-skill-verification"
    },
    {
      title: "Stress Response & Throughput: Measuring Technical Resilience",
      date: "May 2024",
      category: "Resilience",
      excerpt: "Statistical analysis of 'Decision Fatigue' in technical interviews and how asynchronous environments allow for higher cognitive throughput without interviewer bias.",
      tags: ["Pressure Handling", "Throughput", "Resilience"],
      slug: "stress-response-resilience"
    },
    {
      title: "The Syntax of Reasoning: Linguistic Analysis of Multi-Step Logic",
      date: "June 2024",
      category: "NLP",
      excerpt: "Modeling the correlation between linguistic precision and architectural logic. How GreenTick identifies 'Clear Thinkers' through semantic density analysis.",
      tags: ["Communication", "Reasoning", "Linguistics"],
      slug: "syntax-of-reasoning"
    },
    {
      title: "Markers of Cultural Alignment in Professional Ecosystems",
      date: "July 2024",
      category: "Cultural Signal",
      excerpt: "Using NLP to identify markers of ownership, curiosity, and collaboration. Moving away from 'Likeability' to merit-based 'Value-Signal Matching'.",
      tags: ["Cultural Fit", "Alignment", "NLP"],
      slug: "cultural-alignment-markers"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      <Navbar />
      <OrganizationJsonLd />
      <GlossaryJsonLd />
      <BreadcrumbsJsonLd 
        items={[
          { name: 'Home', item: '/' },
          { name: 'Research Lab', item: '/research' }
        ]}
      />

      <main className="pt-32 pb-24 home-section-bg">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-grid-pattern opacity-40 pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(20,184,166,0.1),transparent_50%)] pointer-events-none"></div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Header Section */}
          <div className="max-w-4xl mx-auto text-center mb-24">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 text-teal-400 text-xs font-bold uppercase tracking-[0.2em] rounded-full border border-teal-500/20 mb-8 animate-slide-in">
              <BookOpen className="w-4 h-4" />
              GreenTick Research Lab
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 tracking-tighter italic">
              We don't capture answers. <br />
              <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent not-italic uppercase tracking-tighter">We capture human potential.</span>
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto font-medium leading-relaxed">
              GreenTick Research Lab focuses on the 'hidden work' — the reasoning patterns and mental latency that truly define senior-level potential.
            </p>
          </div>

          {/* Behavioral Signals Grid */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-32">
            {[
              { label: "Reasoning Patterns", desc: "Decomposing complex logic chains" },
              { label: "Communication Clarity", desc: "Technical articulation under load" },
              { label: "Role-Specific Thinking", desc: "Domain-specific mental models" },
              { label: "Pressure Handling", desc: "Throughput under cognitive latency" },
              { label: "Cultural Fit Signals", desc: "Professional value alignment" }
            ].map((signal, i) => (
              <div key={i} className="glass p-6 rounded-2xl border border-white/5 text-center group hover:bg-teal-500/5 transition-all">
                <div className="w-10 h-10 rounded-full bg-teal-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-teal-500/20 transition-all text-xs font-bold text-teal-400">
                  0{i + 1}
                </div>
                <div className="text-sm font-bold text-white mb-2 tracking-tight group-hover:text-teal-400 transition-colors uppercase italic">{signal.label}</div>
                <div className="text-[10px] text-slate-400 leading-relaxed font-medium">{signal.desc}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-32 border-t border-white/5 pt-16">
            {[
              { icon: Binary, label: "Core Methodology", val: "N-Step Inference" },
              { icon: ShieldCheck, label: "Verification Accuracy", val: "99.4%" },
              { icon: Zap, label: "Decision Latency", val: "< 48 Hours" },
              { icon: BarChart3, label: "Bias Reduction", val: "85% improvement" }
            ].map((stat, i) => (
              <div key={i} className="glass p-6 rounded-2xl border border-white/5 text-center group hover:bg-white/5 transition-all">
                <stat.icon className="w-8 h-8 text-teal-500 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">{stat.label}</div>
                <div className="text-lg font-bold text-white">{stat.val}</div>
              </div>
            ))}
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <h2 className="text-3xl font-bold text-white tracking-tight italic">Technical Papers</h2>
              <div className="h-px flex-1 bg-gradient-to-r from-teal-500/50 to-transparent mx-8 opacity-20"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-32">
              {papers.map((paper, i) => (
                <ResearchPaper key={i} {...paper} />
              ))}
            </div>

            {/* Methodology Teaser */}
            <div className="glass rounded-[40px] p-12 md:p-20 border border-white/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-teal-500/10 to-transparent"></div>
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                   <h2 className="text-4xl font-bold text-white mb-4 tracking-tight">Open Methodology Initiative</h2>
                   <p className="text-lg text-slate-400 mb-4 leading-relaxed">
                     We believe transparency is key to ethical AI. That's why we're open-sourcing our scoring frameworks and bias-mitigation algorithms for community review.
                   </p>
                   <p className="text-sm font-bold text-teal-400 uppercase tracking-widest mb-8">For Access Framework</p>
                   <div className="flex flex-wrap gap-4">
                      <a href="mailto:sandesh@greentick.org.in?subject=Inquiry%3A%20GreenTick%20Open%20Methodology%20Framework%20Access&body=Hello%20Sandesh%2C%0A%0AI%20am%20interested%20in%20gaining%20access%20to%20the%20GreenTick%20Open%20Methodology%20Framework.%20%5BAdd%20details%20about%20your%20organization%2Fuse%20case%20here%5D.%0A%0ABest%20regards%2C" className="px-8 py-4 bg-white text-slate-950 rounded-xl font-bold hover:bg-teal-400 transition-all uppercase tracking-widest text-xs">Inquire About the Framework</a>
                      <a href="mailto:sandesh@greentick.org.in?subject=Interest%3A%20GreenTick%20Research%20Advisory%20Board&body=Hello%20Sandesh%2C%0A%0AI%20am%20interested%20in%20joining%20the%20GreenTick%20Research%20Advisory%20Board.%20I%20have%20a%20background%20in%20%5BAdd%20background%20info%5D.%0A%0ABest%20regards%2C" className="px-8 py-4 bg-white/5 border border-white/10 text-white rounded-xl font-bold hover:bg-white/10 transition-all uppercase tracking-widest text-xs">Join Advisory Board</a>
                   </div>
                </div>
                <div className="hidden lg:block">
                   <div className="relative aspect-square">
                      <div className="absolute inset-0 border-2 border-dashed border-teal-500/20 rounded-full animate-spin-slow"></div>
                      <div className="absolute inset-8 border-2 border-dashed border-teal-500/40 rounded-full animate-spin-reverse-slow"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                         <div className="w-32 h-32 bg-teal-500/20 rounded-full blur-2xl animate-pulse"></div>
                         <Binary className="w-16 h-16 text-teal-400 relative z-10" />
                      </div>
                   </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
